import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConceptos, useObligaciones } from '@/hooks/useFlujo';
import { cliente } from '@/lib/cliente';
import type { Obligacion } from '@adelante/shared';
import { formatearColones, formatearDolares } from '@adelante/shared';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Calendar, Edit, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type ObligacionConDetalle = Obligacion & { conceptoNombre: string; moneda: string };

// Reemplaza el <input type="month">: su selector nativo es inconsistente entre navegadores
// (en algunos no abre ningún desplegable) y dentro de un Dialog es todavía más frágil. Un
// Select + input numérico es el mismo patrón que ya usa "Concepto" en este mismo diálogo.
const MESES = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

function formatearDescripcionPeriodo(fecha: string): string {
  const d = new Date(`${fecha}T12:00:00`);
  const desc = d.toLocaleDateString('es-CR', { month: 'long', year: 'numeric' });
  return desc.charAt(0).toUpperCase() + desc.slice(1);
}

// ─── Dialog agregar obligación ────────────────────────────────────────────────

function DialogNuevaObligacion({
  conceptoIdInicial,
  open,
  onClose,
}: {
  conceptoIdInicial?: number;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: conceptos } = useConceptos();
  const conceptosConObl = conceptos?.filter((c) => c.tieneObligaciones && c.activo) ?? [];

  const [form, setForm] = useState({
    conceptoId: conceptoIdInicial ? String(conceptoIdInicial) : '',
    periodoDescripcion: '',
    mes: '',
    anio: String(new Date().getFullYear()),
    monto: '',
  });
  const [guardando, setGuardando] = useState(false);

  const conceptoSeleccionado = conceptos?.find((c) => c.id === Number(form.conceptoId));

  const periodoFecha = form.mes && /^\d{4}$/.test(form.anio) ? `${form.anio}-${form.mes}-01` : '';

  // Sugerir descripción al completar mes + año, sin pisar una que el usuario ya haya escrito
  const actualizarPeriodo = (mes: string, anio: string) => {
    setForm((f) => {
      const fecha = mes && /^\d{4}$/.test(anio) ? `${anio}-${mes}-01` : '';
      const periodoDescripcion =
        fecha && !f.periodoDescripcion ? formatearDescripcionPeriodo(fecha) : f.periodoDescripcion;
      return { ...f, mes, anio, periodoDescripcion };
    });
  };

  // Auto-llenar monto si el concepto tiene cuota fija
  const handleConcepto = (id: string) => {
    const c = conceptos?.find((x) => x.id === Number(id));
    setForm((f) => ({
      ...f,
      conceptoId: id,
      monto: c?.montoCuotaFijo ? String(c.montoCuotaFijo) : f.monto,
    }));
  };

  const handleGuardar = async () => {
    if (!form.conceptoId || !form.periodoDescripcion || !periodoFecha) {
      toast.error('Concepto, período y fecha son requeridos');
      return;
    }
    setGuardando(true);
    try {
      const monto = form.monto ? Number(form.monto.replace(/[₡$,\s]/g, '')) : null;
      const res = await cliente.obligaciones.crear({
        conceptoId: Number(form.conceptoId),
        periodoDescripcion: form.periodoDescripcion,
        periodoFecha,
        monto: monto && !Number.isNaN(monto) ? monto : null,
      });
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Obligación registrada');
      qc.invalidateQueries({ queryKey: ['obligaciones'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Registrar obligación</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Concepto *</Label>
            <Select value={form.conceptoId} onValueChange={handleConcepto}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                {conceptosConObl.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.moneda === 'C' ? '₡' : '$'} {c.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Mes / Período *</Label>
              <div className="flex gap-2">
                <Select value={form.mes} onValueChange={(mes) => actualizarPeriodo(mes, form.anio)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Mes..." />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={form.anio}
                  onChange={(e) => actualizarPeriodo(form.mes, e.target.value)}
                  placeholder="Año"
                  className="w-20"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Descripción *</Label>
              <Input
                value={form.periodoDescripcion}
                onChange={(e) => setForm((f) => ({ ...f, periodoDescripcion: e.target.value }))}
                placeholder="Enero 2026"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>
              Monto{' '}
              {conceptoSeleccionado?.montoCuotaFijo
                ? '(pre-llenado de cuota fija)'
                : '(dejar vacío si es variable)'}
            </Label>
            <Input
              value={form.monto}
              onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))}
              placeholder="0"
              className="tabular-nums"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Registrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Dialog editar obligación ─────────────────────────────────────────────────

function DialogEditarObligacion({
  obl,
  open,
  onClose,
}: {
  obl: ObligacionConDetalle;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    periodoDescripcion: obl.periodoDescripcion,
    monto: obl.monto !== null ? String(obl.monto) : '',
    notas: obl.notas ?? '',
  });
  const [guardando, setGuardando] = useState(false);

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const clean = form.monto.trim().replace(/[₡$,\s]/g, '');
      const monto = clean === '' ? null : Number(clean);
      const res = await cliente.obligaciones.actualizar(obl.id, {
        monto: monto !== null && !Number.isNaN(monto) ? monto : null,
        notas: form.notas.trim() || null,
      });
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Obligación actualizada');
      qc.invalidateQueries({ queryKey: ['obligaciones'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Editar obligación — {obl.conceptoNombre}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Período</Label>
            <Input value={form.periodoDescripcion} disabled className="bg-muted text-sm" />
          </div>
          <div className="space-y-1">
            <Label>Monto (dejar vacío si no lo tenés aún)</Label>
            <Input
              value={form.monto}
              onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))}
              placeholder="Dejar en blanco si es variable"
              className="tabular-nums"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label>Notas</Label>
            <Input
              value={form.notas}
              onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
              placeholder="Observaciones opcionales"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Fila de obligación ───────────────────────────────────────────────────────

function FilaObligacion({
  obl,
  onEliminar,
}: {
  obl: ObligacionConDetalle;
  onEliminar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const fmtMonto =
    obl.monto !== null
      ? obl.moneda === 'C'
        ? formatearColones(obl.monto)
        : formatearDolares(obl.monto)
      : null;

  return (
    <>
      <div className="flex items-center gap-3 px-3 py-2 border-b last:border-0">
        <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{obl.periodoDescripcion}</p>
          <p className="text-xs text-muted-foreground">{obl.conceptoNombre}</p>
          {obl.notas && <p className="text-xs text-muted-foreground/60 italic">{obl.notas}</p>}
        </div>
        {fmtMonto === null ? (
          <Badge variant="warning" className="text-xs gap-1">
            <AlertCircle className="h-3 w-3" />
            Falta monto
          </Badge>
        ) : (
          <span className="text-sm tabular-nums font-medium">{fmtMonto}</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => setEditando(true)}
        >
          <Edit className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive shrink-0"
          onClick={onEliminar}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {editando && (
        <DialogEditarObligacion obl={obl} open={editando} onClose={() => setEditando(false)} />
      )}
    </>
  );
}

// ─── Pantalla principal ───────────────────────────────────────────────────────

export function ObligacionesPantalla() {
  const qc = useQueryClient();
  const { data: obligaciones, isLoading } = useObligaciones({ pendientes: true });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const handleEliminar = async (id: number) => {
    if (!confirm('¿Eliminar esta obligación?')) return;
    try {
      const res = await cliente.obligaciones.eliminar(id);
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Obligación eliminada');
      qc.invalidateQueries({ queryKey: ['obligaciones'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error');
    }
  };

  // Filtrar por búsqueda y agrupar por concepto
  const filtradas = (obligaciones ?? []).filter((o) => {
    if (!busqueda.trim()) return true;
    const t = busqueda.toLowerCase();
    return (
      o.conceptoNombre.toLowerCase().includes(t) || o.periodoDescripcion.toLowerCase().includes(t)
    );
  });

  const porConcepto = new Map<string, ObligacionConDetalle[]>();
  for (const o of filtradas) {
    if (!porConcepto.has(o.conceptoNombre)) porConcepto.set(o.conceptoNombre, []);
    porConcepto.get(o.conceptoNombre)!.push(o as ObligacionConDetalle);
  }

  const faltanMonto = (obligaciones ?? []).filter((o) => o.monto === null).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Obligaciones Periódicas</h1>
          <p className="text-sm text-muted-foreground">
            {obligaciones?.length ?? 0} cuotas pendientes
            {faltanMonto > 0 && (
              <span className="ml-2 text-amber-600">· {faltanMonto} sin monto</span>
            )}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Input
            placeholder="Buscar concepto o período..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-56"
          />
          <Button className="gap-2" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" /> Registrar
          </Button>
        </div>
      </div>

      {faltanMonto > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Hay {faltanMonto} obligación(es) sin monto. Editá cada una para ingresar el monto real
            antes de programar su pago.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="text-muted-foreground text-sm">Cargando...</div>
      ) : porConcepto.size === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay obligaciones pendientes.</p>
          <p className="text-xs mt-1">
            Registrá las cuotas de CCSS, IVA, etc. que están acumuladas.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {Array.from(porConcepto.entries()).map(([nombre, obls]) => {
            const totalColones = obls
              .filter((o) => o.moneda === 'C')
              .reduce((s, o) => s + (o.monto ?? 0), 0);
            const totalDolares = obls
              .filter((o) => o.moneda === 'D')
              .reduce((s, o) => s + (o.monto ?? 0), 0);
            return (
              <div key={nombre} className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/40">
                  <span className="text-xs font-semibold uppercase tracking-wide">{nombre}</span>
                  <div className="flex items-center gap-2 text-xs tabular-nums text-muted-foreground">
                    <Badge variant="secondary" className="text-xs">
                      {obls.length} cuota{obls.length > 1 ? 's' : ''}
                    </Badge>
                    {totalColones > 0 && (
                      <span className="font-medium text-foreground">
                        {formatearColones(totalColones)}
                      </span>
                    )}
                    {totalDolares > 0 && (
                      <span className="font-medium text-foreground">
                        {formatearDolares(totalDolares)}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  {obls.map((o) => (
                    <FilaObligacion
                      key={o.id}
                      obl={o as ObligacionConDetalle}
                      onEliminar={() => handleEliminar(o.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DialogNuevaObligacion
        key={dialogOpen ? 'open' : 'closed'}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}
